<template>
  <div class="adv-chat" :class="{ 'is-open': open }">
    <!-- Bulle d'invite (auto au mount, fermable, persistée via localStorage) -->
    <transition name="adv-fade">
      <div v-if="bubbleVisible && !open" class="adv-bubble">
        <span class="adv-bubble-text">
          Bonjour, je suis <strong>{{ agent.prenom }}</strong>&nbsp;!<br/>
          Une question&nbsp;? Je suis là pour vous aider.
        </span>
        <button type="button" class="adv-bubble-close"
                @click.stop="dismissBubble" aria-label="Fermer">
          <i class="fas fa-times"></i>
        </button>
        <span class="adv-bubble-arrow" aria-hidden="true"></span>
      </div>
    </transition>

    <!-- Avatar replié (clic = ouvre le panneau) -->
    <button v-if="!open" type="button" class="adv-launcher"
            @click="openPanel" :aria-label="`Discuter avec ${agent.prenom}`">
      <img :src="$api.link(agent.avatar)" :alt="`${agent.prenom} ${agent.nom}`" class="adv-launcher-avatar" />
      <span class="adv-launcher-status" aria-hidden="true"></span>
    </button>

    <!-- Panneau ouvert (avatar + actions) -->
    <transition name="adv-pop">
      <div v-if="open" class="adv-panel" role="dialog" :aria-label="`Conseiller ${agent.prenom} ${agent.nom}`">
        <div class="adv-panel-head">
          <img :src="$api.link(agent.avatar)" :alt="`${agent.prenom} ${agent.nom}`" class="adv-panel-avatar" />
          <div class="adv-panel-id">
            <span class="adv-panel-name">{{ agent.prenom }} {{ agent.nom }}</span>
            <span class="adv-panel-role">
              <span class="adv-status-dot" aria-hidden="true"></span>
              Votre conseiller dédié
            </span>
          </div>
          <button type="button" class="adv-panel-close" @click="closePanel" aria-label="Fermer">
            <i class="fas fa-times"></i>
          </button>
        </div>

        <p class="adv-panel-intro">
          Vous avez besoin d'aide ou de précisions&nbsp;?
          N'hésitez pas à me contacter directement.
        </p>

        <div class="adv-panel-actions">
          <a :href="`tel:${agent.telephone}`" class="adv-action adv-action-call">
            <span class="adv-action-icon"><i class="fas fa-phone-alt text-white"></i></span>
            <span class="adv-action-text">
              <strong>Appeler</strong>
              <small>{{ agent.telephone }}</small>
            </span>
          </a>
          <a v-if="agent.whatsapp" :href="`https://wa.me/${agent.whatsapp}`" target="_blank" rel="noopener"
             class="adv-action adv-action-wa">
            <span class="adv-action-icon"><i class="fab fa-whatsapp text-white"></i></span>
            <span class="adv-action-text">
              <strong>WhatsApp</strong>
              <small>Discuter par messagerie</small>
            </span>
          </a>
        </div>

        <small class="adv-panel-hours">
          <i class="far fa-clock"></i>
          Du lundi au vendredi, 9h&nbsp;-&nbsp;19h
        </small>
      </div>
    </transition>
  </div>
</template>

<script>
const BUBBLE_DELAY_MS = 15000;

export default {
  name: 'AdvisorChat',
  props: {
    agent: { type: Object, required: true },
  },
  data() {
    return {
      open: false,
      bubbleVisible: false,
      bubbleTimer: null,
    }
  },
  mounted() {
    this.bubbleTimer = setTimeout(() => {
      this.bubbleVisible = true;
    }, BUBBLE_DELAY_MS);
  },
  beforeDestroy() {
    if (this.bubbleTimer) clearTimeout(this.bubbleTimer);
  },
  methods: {
    openPanel() {
      this.open = true;
      this.bubbleVisible = false;
    },
    closePanel() {
      this.open = false;
    },
    dismissBubble() {
      this.bubbleVisible = false;      
    }
  }
}
</script>

<style scoped>
.adv-chat {
  position: fixed;
  bottom: 24px;
  right: 24px;
  z-index: 95;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
}

/* ---------- Launcher (avatar replié) ---------- */
.adv-launcher {
  position: relative;
  width: 76px; height: 76px;
  border-radius: 50%;
  border: 4px solid #fff;
  background: #fff;
  padding: 0;
  cursor: pointer;
  box-shadow: 0 8px 24px rgba(20, 40, 80, 0.22);
  transition: transform .2s;
  overflow: visible;
}
.adv-launcher:hover { transform: translateY(-3px); }
.adv-launcher-avatar {
  width: 100%; height: 100%;
  object-fit: cover;
  border-radius: 50%;
  display: block;
}
.adv-launcher-status {
  position: absolute;
  bottom: 2px; right: 2px;
  width: 18px; height: 18px;
  background: #00A85E;
  border: 3px solid #fff;
  border-radius: 50%;
  box-shadow: 0 0 0 0 rgba(0, 168, 94, .55);
  animation: adv-pulse 2s infinite;
}
@keyframes adv-pulse {
  0% { box-shadow: 0 0 0 0 rgba(0, 168, 94, .55); }
  70% { box-shadow: 0 0 0 10px rgba(0, 168, 94, 0); }
  100% { box-shadow: 0 0 0 0 rgba(0, 168, 94, 0); }
}

/* ---------- Bulle d'invite ---------- */
.adv-bubble {
  position: absolute;
  bottom: calc(100% + 14px);
  right: 0;
  background: #fff;
  color: #142850;
  padding: 14px 38px 14px 16px;
  border-radius: 16px;
  box-shadow: 0 10px 30px rgba(20, 40, 80, 0.18);
  font-size: 14px;
  line-height: 1.4;
  max-width: 280px;
  min-width: 220px;
}
.adv-bubble-text { display: block; }
.adv-bubble-text strong { color: #1E5BC6; }
.adv-bubble-close {
  position: absolute;
  top: 6px; right: 6px;
  width: 24px; height: 24px;
  border-radius: 50%;
  border: none;
  background: #F4F7FC;
  color: #8090AB;
  cursor: pointer;
  font-size: 12px;
  display: flex; align-items: center; justify-content: center;
  transition: all .15s;
}
.adv-bubble-close:hover { background: #E13B3B; color: #fff; }
.adv-bubble-arrow {
  position: absolute;
  bottom: -7px; right: 32px;
  width: 14px; height: 14px;
  background: #fff;
  transform: rotate(45deg);
  box-shadow: 3px 3px 6px rgba(20, 40, 80, 0.08);
}

/* ---------- Panneau ouvert ---------- */
.adv-panel {
  width: 320px;
  background: #fff;
  border-radius: 20px;
  box-shadow: 0 16px 48px rgba(20, 40, 80, 0.22);
  overflow: hidden;
}
.adv-panel-head {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px;
  background: linear-gradient(135deg, #1E5BC6 0%, #144A9F 100%);
  color: #fff;
}
.adv-panel-avatar {
  width: 52px; height: 52px;
  border-radius: 50%;
  border: 3px solid #fff;
  object-fit: cover;
  flex-shrink: 0;
}
.adv-panel-id {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-width: 0;
}
.adv-panel-name {
  font-weight: 800;
  font-size: 16px;
  line-height: 1.2;
}
.adv-panel-role {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  opacity: .9;
  margin-top: 2px;
}
.adv-status-dot {
  width: 8px; height: 8px;
  border-radius: 50%;
  background: #00ff95;
  box-shadow: 0 0 0 0 rgba(0, 255, 149, .6);
  animation: adv-pulse-light 2s infinite;
}
@keyframes adv-pulse-light {
  0% { box-shadow: 0 0 0 0 rgba(0, 255, 149, .6); }
  70% { box-shadow: 0 0 0 6px rgba(0, 255, 149, 0); }
  100% { box-shadow: 0 0 0 0 rgba(0, 255, 149, 0); }
}
.adv-panel-close {
  width: 32px; height: 32px;
  border: none;
  border-radius: 50%;
  background: rgba(255, 255, 255, .18);
  color: #fff;
  cursor: pointer;
  font-size: 14px;
  display: flex; align-items: center; justify-content: center;
  transition: background-color .15s;
  flex-shrink: 0;
}
.adv-panel-close:hover { background: rgba(255, 255, 255, .35); }

.adv-panel-intro {
  margin: 0;
  padding: 16px 18px 10px;
  font-size: 14px;
  color: #4A5A78;
  line-height: 1.5;
}

.adv-panel-actions {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 0 18px 14px;
}
.adv-action {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 14px;
  border-radius: 14px;
  text-decoration: none;
  transition: transform .15s, box-shadow .15s;
}
.adv-action:hover { transform: translateY(-1px); text-decoration: none; }
.adv-action-icon {
  width: 40px; height: 40px;
  border-radius: 50%;
  display: flex; align-items: center; justify-content: center;
  color: #fff;
  font-size: 18px;
  flex-shrink: 0;
}
.adv-action-text {
  display: flex;
  flex-direction: column;
  line-height: 1.25;
  min-width: 0;
}
.adv-action-text strong {
  font-size: 15px;
  font-weight: 800;
}
.adv-action-text small {
  font-size: 12px;
  opacity: .85;
}
/* Appel */
.adv-action-call {
  background: #E8F0FE;
  color: #1E5BC6;
  box-shadow: 0 2px 8px rgba(30, 91, 198, .15);
}
.adv-action-call:hover { background: #DCE7FB; color: #144A9F; box-shadow: 0 6px 16px rgba(30, 91, 198, .25); }
.adv-action-call .adv-action-icon { background: #1E5BC6; }
/* WhatsApp */
.adv-action-wa {
  background: #E5F8EE;
  color: #008a4f;
  box-shadow: 0 2px 8px rgba(37, 211, 102, .18);
}
.adv-action-wa:hover { background: #D4F3E1; color: #006a3d; box-shadow: 0 6px 16px rgba(37, 211, 102, .3); }
.adv-action-wa .adv-action-icon { background: #25d366; font-size: 22px; }

.adv-panel-hours {
  display: block;
  text-align: center;
  font-size: 12px;
  color: #8090AB;
  padding: 0 18px 16px;
}
.adv-panel-hours i { margin-right: 4px; }

/* ---------- Transitions ---------- */
.adv-fade-enter-active, .adv-fade-leave-active { transition: opacity .25s, transform .25s; }
.adv-fade-enter, .adv-fade-leave-to { opacity: 0; transform: translateY(8px); }
.adv-pop-enter-active, .adv-pop-leave-active { transition: opacity .25s, transform .25s; }
.adv-pop-enter, .adv-pop-leave-to { opacity: 0; transform: scale(.94) translateY(12px); transform-origin: bottom right; }

/* ---------- Responsive ---------- */
@media (max-width: 560px) {
  .adv-chat { bottom: 16px; right: 16px; }
  .adv-launcher { width: 64px; height: 64px; }
  .adv-launcher-status { width: 16px; height: 16px; }
  .adv-panel { width: calc(100vw - 32px); max-width: 340px; }
  .adv-bubble { max-width: 240px; font-size: 13px; }
}
</style>
